<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBytes } from 'shared/helpers/FileHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['removeAttachment', 'retryAttachment']);

const { t } = useI18n();

const nonRecordedAudioAttachments = computed(() => {
  return props.attachments.filter(attachment => !attachment?.isVoiceMessage);
});

const recordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => attachment.isVoiceMessage)
);

const onRemoveAttachment = itemIndex => {
  emit(
    'removeAttachment',
    nonRecordedAudioAttachments.value
      .filter((_, index) => index !== itemIndex)
      .concat(recordedAudioAttachments.value)
  );
};

const onRetryAttachment = attachment => {
  emit('retryAttachment', attachment);
};

const formatFileSize = file => {
  const size = file.byte_size || file.size;
  return formatBytes(size, 0);
};

const isTypeImage = file => {
  const type = file.content_type || file.type;
  return type.includes('image');
};

const fileName = file => {
  return file.filename || file.name;
};

const extensionOf = file => {
  const name = fileName(file) || '';
  return name.includes('.') ? name.split('.').pop() : '';
};

const isUploading = attachment => attachment.status === 'uploading';
const isFailed = attachment => attachment.status === 'failed';
</script>

<template>
  <div class="flex flex-wrap gap-y-1 gap-x-2 overflow-auto max-h-[12.5rem]">
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.uploadId || attachment.id"
      class="relative flex items-center p-1 bg-n-slate-3 gap-1 rounded-md w-[15rem] overflow-hidden"
      :class="{ 'opacity-70': isFailed(attachment) }"
    >
      <div
        v-if="isUploading(attachment)"
        class="absolute bottom-0 left-0 h-0.5 bg-n-brand transition-all duration-150"
        :style="{ width: `${attachment.progress || 0}%` }"
      />
      <div class="max-w-[4rem] flex-shrink-0 w-6 flex items-center">
        <img
          v-if="isTypeImage(attachment.resource) && attachment.thumb"
          class="object-cover w-6 h-6 rounded-sm"
          :src="attachment.thumb"
        />
        <FileIcon
          v-else
          :file-type="extensionOf(attachment.resource)"
          class="size-5"
        />
      </div>
      <div class="max-w-3/5 min-w-[50%] overflow-hidden text-ellipsis">
        <span
          class="h-4 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap block"
        >
          {{ fileName(attachment.resource) }}
        </span>
        <span v-if="isFailed(attachment)" class="text-xs text-n-ruby-9">
          {{ t('CONVERSATION.ATTACHMENT_UPLOAD_FAILED') }}
        </span>
      </div>
      <div v-if="!isFailed(attachment)" class="w-[30%] justify-center">
        <span class="overflow-hidden text-xs text-ellipsis whitespace-nowrap">
          {{ formatFileSize(attachment.resource) }}
        </span>
      </div>
      <div class="flex items-center justify-center gap-0.5 shrink-0">
        <Button
          v-if="isFailed(attachment)"
          ghost
          slate
          xs
          icon="i-lucide-rotate-cw"
          :aria-label="t('CONVERSATION.RETRY_UPLOAD')"
          @click="onRetryAttachment(attachment)"
        />
        <Button
          ghost
          slate
          xs
          icon="i-lucide-x"
          :aria-label="t('CONVERSATION.REMOVE_ATTACHMENT')"
          @click="onRemoveAttachment(index)"
        />
      </div>
    </div>
  </div>
</template>
